import type { VNode, CreateElement } from 'vue';
import Vue from 'vue';
import { Component, Inject, Prop, Watch } from 'vue-property-decorator';
import { loadScriptWithGlobal } from '../../util/script-loader';
import { getCompositeBgColor } from '../../util/color';
import type { PaymentProcessorCardComponent, PaymentFieldStatus } from 'src/framework/payment/payment-types';
import type { CultureInfo } from '@cognitoforms/model.js';
import { preventVueObservability } from '@cognitoforms/vuemodel';
import type { PaymentFormStyle } from './payment-form-style';
import type { Address } from '@stripe/stripe-js/types/api/shared';
import type { Stripe, StripeConstructor, StripeError, PaymentIntentResult, SetupIntentResult, PaymentMethodResult } from '@stripe/stripe-js/types/stripe-js/stripe';
import type { StripeElementsPaymentOptions, StripeElementsSetupOptions } from 'src/@types/stripe-js.d';
import type { Appearance, StripeElementLocale, StripeElements, StripeElementsOptions } from '@stripe/stripe-js/types/stripe-js/elements-group';
import type { StripePaymentElement, StripePaymentElementOptions, StripePaymentElementChangeEvent } from '@stripe/stripe-js/types/stripe-js/elements/payment';
import { toCents } from 'src/util/currency';
import type Log from 'src/framework/logging/log';
import { getCssVarValue } from '@cognitoforms/utils/css-helper';

// https://stripe.com/docs/api/payment_methods/create#create_payment_method-billing_details
export type BillingDetails = {
	address?: Address,
	email?: string,
	name?: string,
	phone?: string
};

export type StripePaymentElementEvent = {
	elementType: 'payment'
};

export enum PaymentMethodType {
	None = 'None',
	Card = 'Card',
	GooglePay = 'GooglePay',
	ApplePay = 'ApplePay'
}

export const StripeMinimumAmounts = Object.freeze({
	'usd': 0.50,
	'aed': 2.00,
	'aud': 0.50,
	'bgn': 1.00,
	'brl': 0.50,
	'cad': 0.50,
	'chf': 0.50,
	'czk': 15.00,
	'dkk': 2.50,
	'eur': 0.50,
	'gbp': 0.30,
	'hkd': 4.00,
	'hrk': 0.50,
	'huf': 175.00,
	'inr': 0.50,
	'jpy': 50,
	'mxn': 10,
	'myr': 2,
	'nok': 3.00,
	'nzd': 0.50,
	'pln': 2.00,
	'ron': 2.00,
	'sek': 3.00,
	'sgd': 0.50,
	'thb': 10
});

export type StripePaymentElementErrorEvent = StripePaymentElementEvent & {
	error: StripeError;
};

let stripeStatic: StripeConstructor = null;
let stripeStaticPromise: Promise<StripeConstructor | void> = null;

async function ensureStripeJS(targetElement: Element = null): Promise<StripeConstructor | void> {
	if (stripeStatic)
		return stripeStatic;
	if (!stripeStaticPromise) {
		stripeStaticPromise = loadScriptWithGlobal<StripeConstructor>('https://js.stripe.com/v3/', 'Stripe', targetElement).then(Stripe => {
			stripeStatic = Stripe;
			stripeStaticPromise = null;
			return Stripe;
		}).catch(error => {
			// Null out the promise field so that it could be re-attempted
			stripeStaticPromise = null;
			// Re-raise the original error
			throw error;
		});
	}
	return stripeStaticPromise;
}

@Component
export default class StripeCard extends Vue implements PaymentProcessorCardComponent {
	@Prop({ type: String, required: true })
	publishableKey: string;

	@Prop({ type: String, required: false, default: null })
	connectedAccountId: string;

	@Prop({ type: Object, default: () => {} })
	inputStyles: PaymentFormStyle;

	@Prop({ type: String, required: true })
	locale: string;

	@Prop({ type: Boolean, default: false })
	saveCard: boolean;

	@Prop({ type: Boolean, default: false })
	paymentRequired: boolean;

	/**
	 * The amount to pay in "cents", i.e. non-fractional
	 */
	@Prop({ type: Number, required: false, default: 0 })
	amount: number;

	@Prop({ type: String, required: true })
	currency: string;

	@Prop({ type: Object, required: false, default: () => {} })
	billingDetails: BillingDetails;

	@Prop({ type: Boolean, default: true })
	useWallets: boolean;

	@Inject()
	$format: (value: number | Date, format: string) => string;

	@Inject()
	$culture : CultureInfo;

	@Inject()
	log: Log;

	stripe: Stripe = null;

	elements: StripeElements = null;

	element: StripePaymentElement = null;

	elementStatus: PaymentFieldStatus = null;

	initializeFormPromise: Promise<StripeElements> = null;

	paymentMethodType: PaymentMethodType = null;

	mounted() {
		setTimeout(() => {
			// Pre-load Stripe to avoid delay when the payment form is shown
			ensureStripeJS().catch(error => {
				// Re-throw the original error
				throw error;
			});
		}, 100);

		// Initialize the form immediately if already shown
		this.initializeForm();
	}

	@Watch('inputStyles.fontUrl')
	onFontUrlChanged(newVal) {
		// only do a full rerender of the payment elements if fontUrl changes to an actual url
		if (this.elements && !!newVal) {
			this.elements = null;
			this.initializeForm();
		}
	}

	@Watch('appearance')
	onAppearanceChanged() {
		if (this.elements) {
			this.elements.update({ appearance: this.appearance });
		}
	}

	@Watch('intentOptions')
	async onIntentOptionsChanged() {
		if (this.elements) {
			if (this.payOrSaveMode)
				this.elements.update(this.intentOptions);
		}
		else
			this.initializeForm();
	}

	@Watch('billingDetails')
	onBillingDetailsUpdate() {
		if (!this.element)
			return;

		// Update what billing details are shown on the payment element
		this.element.update({ fields: this.fieldOptions });
	}

	render(h: CreateElement): VNode {
		let childNodes: VNode[];
		if (this.$scopedSlots && this.$scopedSlots.default) {
			// Render Stripe payment form with provided slot content
			childNodes = this.$scopedSlots.default({});
		}
		else {
			// Render Stripe payment form with default markup
			childNodes = [
				h('div', { attrs: { id: 'cog-payment-element' } })
			];
		}
		return h('div', {
		}, childNodes);
	}

	async getStripeInstance(headElement: Element = null): Promise<Stripe> {
		if (this.stripe) {
			return this.stripe;
		}

		const Stripe = await ensureStripeJS(headElement || this.$el);
		if (!Stripe) {
			throw new Error('Failed to load Stripe.js.');
		}

		const stripeOptions: any = {
			betas: ['elements_enable_deferred_intent_beta_1']
		};

		// For CognitoPay connected accounts, add the stripeAccount parameter
		if (this.connectedAccountId) {
			stripeOptions.stripeAccount = this.connectedAccountId;
		}

		const stripe = Stripe(this.publishableKey, stripeOptions);
		preventVueObservability(stripe);
		this.stripe = stripe;
		return this.stripe;
	}

	async initializeForm(headElement: Element = null): Promise<StripeElements> {
		if (this.elements) {
			// Stripe payment form is already initialized
			return this.elements;
		}

		// Form is currently being initialized
		if (this.initializeFormPromise)
			return this.initializeFormPromise;

		const stripe = await this.getStripeInstance(headElement);

		if (!this.payOrSaveMode)
			return;

		const initializeFormPromise = new Promise<StripeElements>((resolve, reject) => {
			try {
				const elements = stripe.elements(this.elementsOptions);

				const element = elements.create('payment', this.elementOptions);
				preventVueObservability(element);
				this.element = element;
				element.mount(this.$el.firstChild as HTMLElement);

				this.element.on('ready', this.handlePaymentElementReady);
				this.element.on('loaderror', this.handlePaymentElementLoadError);
				this.element.on('loaderstart', this.handlePaymentElementLoaderStart);
				this.element.on('change', this.handlePaymentElementChange);
				this.element.on('focus', this.handlePaymentElementFocus);
				this.element.on('blur', this.handlePaymentElementBlur);

				preventVueObservability(elements);

				this.elements = elements;

				this.initializeFormPromise = null;

				resolve(elements);
			}
			catch (error) {
				// Null out the promise so that loading the form can be re-attempted
				this.initializeFormPromise = null;
				throw error;
			}
		});

		preventVueObservability(initializeFormPromise);

		this.initializeFormPromise = initializeFormPromise;

		return this.initializeFormPromise;
	}

	handlePaymentElementReady(e: StripePaymentElementEvent) {
		this.$emit('ready', e);
	}

	handlePaymentElementLoadError(e: StripePaymentElementErrorEvent) {
		this.$emit('load-error', e);
	}

	handlePaymentElementLoaderStart(e: StripePaymentElementEvent) {
		this.$emit('loader-ready', e);
	}

	handlePaymentElementBlur(e: StripePaymentElementEvent) {
		this.$emit('blur', e);
	}

	handlePaymentElementFocus(e: StripePaymentElementEvent) {
		this.$emit('focus', e);
	}

	handlePaymentElementChange(e: StripePaymentElementChangeEvent) {
		// Update the input's status
		this.elementStatus = { isEmpty: e.empty, isComplete: e.complete };

		// Update the payment method type
		if (e.value) {
			switch (e.value.type) {
				case 'google_pay':
					this.paymentMethodType = PaymentMethodType.GooglePay;
					break;
				case 'apple_pay':
					this.paymentMethodType = PaymentMethodType.ApplePay;
					break;
				case 'card':
					this.paymentMethodType = PaymentMethodType.Card;
					break;
				default:
					this.paymentMethodType = PaymentMethodType.None;
			}
		}
		else {
			this.paymentMethodType = PaymentMethodType.None;
		}

		// Broadcast change event so that outside subscribers can know that the CC info has changed (i.e. a new token is required)
		this.$emit('change', e);
	}

	/**
	 * Gets the intent "mode" to use when constructing the elements object.
	 * Possible values are "payment" (which also supports saving the card via
	 * the `setup_future_usage` option) or "setup" (to save the card for future use).
	 * @link https://stripe.com/docs/payments/defer-intent-creation?type=payment#additional-options
	 */
	get payOrSaveMode(): 'payment' | 'setup' | undefined {
		if (this.amount > 0 && this.paymentRequired)
			return 'payment';
		else if (this.saveCard)
			return 'setup';
	}

	hasBillingAddress(billingInfo: BillingDetails) {
		if (!billingInfo || !billingInfo.address)
			return false;

		const address = billingInfo.address;

		return address.line1 || address.line2 || address.city || address.state || address.postal_code || address.country;
	}

	get elementOptions(): StripePaymentElementOptions {
		const options: StripePaymentElementOptions = {};

		if (this.useWallets) {
			options.wallets = {
				applePay: 'auto',
				googlePay: 'auto'
			};
		}
		else {
			options.wallets = {
				applePay: 'never',
				googlePay: 'never'
			};
		}

		options.fields = this.fieldOptions;

		return options;
	}

	get fieldOptions(): object {
		let billingFieldsOptions:any = 'auto';

		if (this.billingDetails) {
			billingFieldsOptions = {
				name: this.billingDetails.name ? 'never' : 'auto',
				email: this.billingDetails.email ? 'never' : 'auto',
				phone: this.billingDetails.phone ? 'never' : 'auto',
				address: this.hasBillingAddress(this.billingDetails) ? {
					line1: this.billingDetails.address.line1 ? 'never' : 'auto',
					line2: this.billingDetails.address.line2 ? 'never' : 'auto',
					city: this.billingDetails.address.city ? 'never' : 'auto',
					state: this.billingDetails.address.state ? 'never' : 'auto',
					country: this.billingDetails.address.country ? 'never' : 'auto',
					postalCode: this.billingDetails.address.postal_code ? 'never' : 'auto'
				} : 'auto'
			};
		}

		return {
			billingDetails: billingFieldsOptions
		};;
	}

	get elementsOptions(): StripeElementsOptions & (StripeElementsPaymentOptions | StripeElementsSetupOptions) {
		const inputStyles = this.inputStyles;
		const standardOptions: StripeElementsOptions = {
			fonts: inputStyles.fontUrl ? [{ cssSrc: inputStyles.fontUrl }] : [],
			locale: this.locale as StripeElementLocale,
			appearance: this.appearance,
			loader: 'never'
		};
		return Object.assign({}, this.intentOptions, standardOptions);
	}

	get intentOptions(): StripeElementsPaymentOptions | StripeElementsSetupOptions {
		if (this.payOrSaveMode === 'payment') {
			return {
				mode: 'payment',
				amount: this.amountOrMinimum,
				currency: this.currency,
				payment_method_types: ['card'],
				setup_future_usage: this.saveCard ? 'off_session' : null
			};
		}
		else if (this.payOrSaveMode === 'setup') {
			return {
				mode: 'setup',
				payment_method_types: ['card'],
				setup_future_usage: 'off_session'
			};
		}
	}

	get appearance(): Appearance {
		if (Object.keys(this.inputStyles).length === 0)
			return;
		const inputStyles = this.inputStyles;
		const nativeBorderStyles = inputStyles.borderWidth === '2px' && inputStyles.borderStyle === 'inset';

		// Public form styles
		if (inputStyles.useFormTheme) {
			return {
				labels: 'floating',
				theme: 'none',
				variables: {
					colorText: inputStyles.color,
					colorTextSecondary: inputStyles.textColor,
					borderRadius: inputStyles.borderRadius,
					colorBackground: getCompositeBgColor(inputStyles.styleEl),
					spacingUnit: (parseInt(inputStyles.computedStyles['padding-top'], 10) / 3) + 'px',
					colorDanger: inputStyles.negativeColor,
					spacingGridRow: nativeBorderStyles ? '12px' : inputStyles.computedStyles['padding-top'],
					spacingGridColumn: nativeBorderStyles ? '12px' : undefined,
					colorTextPlaceholder: inputStyles.placeholderColor
				},
				rules: {
					// A poor man's display: none.
					'.Label': {
						transition: 'none',
						color: inputStyles.placeholderColor
					},
					'.Label--resting': {
						fontSize: inputStyles.fontSize
					},
					'.Label--floating': {
						fontSize: inputStyles.fontSize,
						color: 'transparent',
						fontSize: '0',
						margin: '0'
					},
					'.Input': {
						borderWidth: nativeBorderStyles ? '1px' : inputStyles.borderWidth,
						borderStyle: nativeBorderStyles ? 'solid' : inputStyles.computedStyles['border-style'],
						borderColor: inputStyles.borderColor,
						boxShadow: inputStyles.boxShadow,
						fontFamily: inputStyles.fontFamily,
						fontSize: inputStyles.fontSize
					},
					'.Input:focus': {
						borderColor: inputStyles.focusStyles.borderColor,
						borderWidth: inputStyles.focusStyles.borderWidth,
						boxShadow: inputStyles.focusStyles.boxShadow,
						outline: 'none'
					},
					'.Error': {
						color: 'transparent',
						fontSize: '0',
						margin: '0'
					},
					'.Tab': {
						borderWidth: nativeBorderStyles || inputStyles.borderStyle === 'dashed' ? '1px' : inputStyles.borderWidth, // if theme is underlined (dashed), we want the tab's borders to be the same style as the default
						borderStyle: nativeBorderStyles || inputStyles.borderStyle === 'dashed' ? 'solid' : inputStyles.computedStyles['border-style'],
						margin: nativeBorderStyles ? '6px' : (parseInt(inputStyles.computedStyles['padding-left'], 10) / 2) + 'px', // if the theme chameleon (inset), set tab margin to be half of the nativeBorder padding, otherwise set it to half of input padding
						boxShadow: '1px 1px 2px rgba(0,0,0,0.075), inset 0 0 0 1px rgba(255, 255, 255, 0.6)',
						backgroundColor: 'transparent',
						borderColor: '#bfbfbf',
						borderRadius: Math.max(3, parseInt(inputStyles.borderRadius)) + 'px', // 3 is the default border-radius for cards
						fontFamily: inputStyles.fontFamily,
						fontSize: inputStyles.fontSize,
						fontWeight: 'normal', // Stripe's default is bold
						color: inputStyles.color
					},
					'.Tab:focus': {
						outline: '1px solid ' + inputStyles.focusStyles.borderColor,
						outlineOffset: '1px'
					},
					'.Tab:hover': {
						outline: `${inputStyles.borderColor === 'rgba(0, 0, 0, 0)' ? '#bfbfbf' : inputStyles.borderColor} solid 1px`, // certain themes have a transparent border color, if thats the case we set the border for the tabs to be a solid 1px
						boxShadow: 'none'
					},
					'.Tab--selected:hover': {
						outline: 'none'
					},
					'.Tab--selected': {
						borderColor: inputStyles.focusStyles.borderColor,
						borderWidth: '2px'
					},
					'.TabIcon': {
						fill: inputStyles.color
					},
					'.TabIcon--selected': {
						fill: inputStyles.focusStyles.borderColor
					}
				}
			};
		}
		// Payment Dialog Styles
		else {
			return {
				theme: 'stripe',
				variables: {
					colorPrimary: getCssVarValue('--text-primary'),
					colorTextSecondary: getCssVarValue('--text-primary'),
					colorBackground: getCssVarValue('--bg-input'),
					colorDanger: getCssVarValue('--border-error'),
					colorTextPlaceholder: getCssVarValue('--text-placeholder'),
					fontSizeBase: '14px',
					borderRadius: '0px',
					spacingGridRow: '18px'
				},
				rules: {
					'.Input': {
						color: getCssVarValue('--text-primary'),
						backgroundColor: getCssVarValue('--bg-input'),
						fontSize: '14px',
						outline: 'none',
						borderWidth: '1px',
						borderColor: getCssVarValue('--border-input')
					},
					'.Input:focus': {
						borderWidth: '1px',
						borderColor: getCssVarValue('--outline-focused'),
						boxShadow: 'transparent',
						outline: 'none'
					},
					'.Input--invalid': {
						color: getCssVarValue('--text-primary')
					},
					'.Label': {
						color: 'transparent',
						fontSize: '0',
						margin: '0'
					},
					'.Error': {
						color: 'transparent',
						fontSize: '0',
						margin: '0'
					}
				}
			};
		}
	}

	get amountOrMinimum() : number {
		return this.amount >= this.minimumAmount ? this.amount : this.minimumAmount;
	}

	/**
	 * Minimum amount represented as a whole number (cents)
	 */
	get minimumAmount(): number {
		return toCents(StripeMinimumAmounts[this.currency] || 0, { NumberOfDecimals: this.$culture.numberFormat.CurrencyDecimalDigits });
	}

	/**
	 * Captures payment method information
	 * @returns The payment method result
	 */
	async capturePaymentMethod(): Promise<PaymentMethodResult> {
		if (this.paymentRequired && (this.paymentMethodType === PaymentMethodType.GooglePay || this.paymentMethodType === PaymentMethodType.ApplePay) && this.amount < this.minimumAmount) {
			return Promise.reject(new Error('Amount must be at least ' + this.$format(StripeMinimumAmounts[this.currency] || 0, 'C') + '.'));
		}

		return this.stripe.createPaymentMethod({
			elements: this.elements,
			params: {
				billing_details: this.billingDetails
			}
		});
	}

	/**
	 * Prompt the user to authenticate the current payment using the provided client secret
	 * */
	authenticatePayment(clientSecret: string): Promise<PaymentIntentResult> {
		// Try to authenticate card
		return this.stripe.handleNextAction({ clientSecret });
	}

	/**
	 * Prompt the user to authenticate the current card using the provided client secret
	 * */
	authenticateCard(clientSecret: string): Promise<SetupIntentResult> {
		if (!clientSecret)
			return;

		// Try to authenticate card
		return this.stripe.confirmCardSetup(clientSecret);
	}
}